<template>
  <Layout>
    <div class="row">
      <PageHeader class="col-10" title="API Management" />
      <div class="col-2">
        <button type="button" @click="showAddApiModal" class="btn btn-info btn-rounded mb-2 me-2 w-100">
          <i class="mdi mdi-plus me-1"></i>
          {{ $t("menuitems.administration.list.api.list.add") }}
        </button>
      </div>
    </div>

    <!-- Company Table -->
    <div class="table-responsive mb-0">
      <b-table class="datatables" :items="apiData" :fields="fields" responsive="sm" :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc" :filter="filter" :filter-included-fields="filterOn">
        <template #cell(created_at)="row">
          <span>{{ new Date(row.item.created_at).toDateString() }}
            {{ new Date(row.item.created_at).toLocaleTimeString() }}</span>
        </template>

        <template #cell(api_type)="row">
          <span :class="`badge ${getApiTypeClass(row.item.api_type)}`">
            {{ row.item.api_type }}</span>
        </template>

        <template #cell(package_id)="row">
          {{ getPackageName(row.item.package_id) }}
        </template>

        <template #cell(actions)="row">
          <b-button size="sm" variant="info" class="m-1" @click="copyAPI(row.item)">
            <i class="fa fa-copy"></i>
            Copy API
          </b-button>
          <b-button size="sm" variant="primary" class="m-1" @click="showUpdateApiModal(row.item)">
            <i class="fa fa-edit"></i>
            Edit
          </b-button>
          <b-button size="sm" variant="danger" class="m-1" @click="deleteApi(row.item)">
            <i class="fa fa-trash"></i>
            Delete
          </b-button>
        </template>
      </b-table>
    </div>

    <!-- Create new vsds modal -->
    <b-modal id="create-new-api-modal" title="Create New Company" title-class="font-18" no-close-on-backdrop hide-footer
      size="lg">
      <template #modal-header>
        <div>
          <h5>Create New API</h5>
        </div>
        <div>
          <button class="btn btn-sm btn-outline-danger" @click="closeAddNewModal()">
            Cancel
          </button>
        </div>
      </template>

      <form @submit.prevent="createNewApi">
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table mb-0">
                    <tbody>
                      <tr>
                        <th scope="row">API Name</th>
                        <td>
                          <input placeholder="Enter API Name" v-model="newApiObj.apiName" type="text"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">API Type</th>
                        <td>
                          <v-select class="style-chooser" v-model="newApiObj.apiType" :options="apiTypes"></v-select>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Package</th>
                        <td>
                          <v-select multiple class="style-chooser" v-model="newApiObj.packageId" :reduce="(r) => r.id"
                            label="package_name" :options="packageMaster"></v-select>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Request Type</th>
                        <td>
                          <v-select class="style-chooser" v-model="newApiObj.requestType"
                            :options="requestTypes"></v-select>
                        </td>
                      </tr>
                      <tr v-if="newApiObj.requestType === 'GET'">
                        <th scope="row">Request URL</th>
                        <td>
                          <input placeholder="http://vehiscan:vehiscan@10.152.78.252/getTransactionsList"
                            v-model="newApiObj.requestPath" type="text" class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr v-if="newApiObj.requestType === 'POST'">
                        <th scope="row">Request URL Path</th>
                        <td>
                          <input minlength="4" maxlength="20" placeholder="pkg3-samajh-vsds"
                            v-model="newApiObj.requestPath" type="text" class="form-control inp-bg" required />
                          <div class="mt-2" v-if="newApiObj.requestPath">
                            <small class="mt-2">Share API Link:</small>
                            <p>
                              <small><strong>{{ config.BASE_URL }}/api/external/push/{{
                                newApiObj.requestPath
                              }}</strong></small>
                            </p>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th class="w-50" scope="row">Image Download Authentication</th>
                        <td>
                          <b-form-group>
                            <b-form-radio-group v-model="newApiObj.isPartialImageUrl" buttons
                              button-variant="outline-primary" size="sm">
                              <b-form-radio value="true"> <span class="p-2">Yes</span></b-form-radio>
                              <b-form-radio value="false"> <span class="p-2">No</span></b-form-radio>
                            </b-form-radio-group>
                          </b-form-group>

                        </td>
                      </tr>
                      <tr v-if="newApiObj.isPartialImageUrl == true">
                        <th scope="row">Auth Username</th>
                        <td>
                          <input placeholder="Download Image Username" v-model="newApiObj.authUsername" type="text"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr v-if="newApiObj.isPartialImageUrl == true">
                        <th scope="row">Auth Password</th>
                        <td>
                          <input placeholder="Download Image Password" v-model="newApiObj.authPassword" type="text"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Create API <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>

    <!-- Edit and Update VSDS -->
    <b-modal id="edit-api-modal" title="Create New Company" title-class="font-18" no-close-on-backdrop hide-footer
      size="lg">
      <template #modal-header>
        <div>
          <h5>Update VSDS</h5>
        </div>
        <div>
          <button class="btn btn-sm btn-outline-danger" @click="$bvModal.hide('edit-api-modal')">
            Cancel
          </button>
        </div>
      </template>

      <form @submit.prevent="updateApi">
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table mb-0">
                    <tbody>
                      <tr>
                        <th scope="row">API Name</th>
                        <td>
                          <input placeholder="Enter API Name" v-model="updateApiObj.api_name" type="text"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">API Type</th>
                        <td>
                          <v-select class="style-chooser" v-model="updateApiObj.api_type" :options="apiTypes"></v-select>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Package</th>
                        <td>
                          <v-select class="style-chooser" v-model="updateApiObj.package_id" :reduce="(r) => r.id"
                            label="package_name" :options="packageMaster"></v-select>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">Request Type</th>
                        <td>
                          <v-select class="style-chooser" v-model="updateApiObj.request_type"
                            :options="requestTypes"></v-select>
                        </td>
                      </tr>
                      <tr v-if="updateApiObj.request_type === 'GET'">
                        <th scope="row">Request URL</th>
                        <td>
                          <input placeholder="http://vehiscan:vehiscan@10.152.78.252/getTransactionsList"
                            v-model="updateApiObj.request_path" type="text" class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr v-if="updateApiObj.request_type === 'POST'">
                        <th scope="row">Request URL Path</th>
                        <td>
                          <input minlength="4" maxlength="20" placeholder="pkg3-samajh-vsds"
                            v-model="updateApiObj.request_path" type="text" class="form-control inp-bg" required />
                          <small class="mt-2">Share API Link:</small>
                          <p>
                            <small><strong>{{ config.BASE_URL }}/api/external/push/{{
                              updateApiObj.request_path
                            }}</strong></small>
                          </p>
                        </td>
                      </tr>
                      <tr>
                        <th class="w-50" scope="row">Image Download Authentication</th>
                        <td>
                          <b-form-group>
                            <b-form-radio-group v-model="updateApiObj.require_img_download" buttons
                              button-variant="outline-primary" size="sm">
                              <b-form-radio value="true"> <span class="p-2">Yes</span></b-form-radio>
                              <b-form-radio value="false"> <span class="p-2">No</span></b-form-radio>
                            </b-form-radio-group>
                          </b-form-group>

                        </td>
                      </tr>
                      <tr v-if="updateApiObj.require_img_download == true">
                        <th scope="row">Auth Username</th>
                        <td>
                          <input placeholder="Download Image Username" v-model="updateApiObj.auth_username" type="text"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                      <tr v-if="updateApiObj.require_img_download == true">
                        <th scope="row">Auth Password</th>
                        <td>
                          <input placeholder="Download Image Password" v-model="updateApiObj.auth_password" type="text"
                            class="form-control inp-bg" required />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="mt-2 text-center">
              <button type="submit" class="btn btn-primary me-1 w-50">
                Update API <i class="bx bx-rocket ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>
<script>
import Layout from "@/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import vsdsMixin from "../../mixins/administration";
import incidentMixin from "../../mixins/incident";
import config from '../../constants/config'
import Swal from "sweetalert2";

/**
 * Dashboard Component
 */
export default {
  page: {
    title: "API Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  mixins: [vsdsMixin, incidentMixin],

  data () {
    return {
      config: config,
      apiData: [],
      requestTypes: ['GET', 'POST'],
      apiTypes: ['VIDS', 'VSDS', 'ATCC'],
      newApiObj: {},
      updateApiObj: {},
      packageMaster: [],
      sortBy: "id",
      sortDesc: false,
      filterOn: [],
      filter: null,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: false,
        },
        {
          key: "api_name",
          label: "API Name",
          sortable: false,
        },
        {
          key: "api_type",
          label: "API Type",
          sortable: false,
        },
        {
          key: "request_type",
          label: "Request Type",
          sortable: false,
        },
        {
          key: "package_id",
          label: "Package",
          sortable: false,
        },
        {
          key: "actions",
          label: "Actions",
          sortable: false,
        },
      ],
    };
  },
  methods: {
    showAddApiModal () {
      this.$bvModal.show("create-new-api-modal");
    },

    copyAPI (data) {
      this.$clipboard(`${config.BASE_URL}/api/external/push/${data.request_path}`)
      this.$toast.info("API Copied to clipboard", {
        position: "bottom-right",
        duration: 2000,
      });
    },

    showUpdateApiModal (apiData) {
      this.updateApiObj = apiData;
      this.$bvModal.show("edit-api-modal");
    },

    closeAddNewModal () {
      this.newApiObj = {}
      this.$bvModal.hide("create-new-api-modal");
    },

    async createNewApi () {
      try {
        let payload = {
          api_name: this.newApiObj.apiName,
          api_type: this.newApiObj.apiType,
          package_id: this.newApiObj.packageId,
          request_type: this.newApiObj.requestType,
          request_path: this.newApiObj.requestPath,
          partial_image_url: this.newApiObj.isPartialImageUrl,
          auth_username: this.newApiObj.authUsername,
          auth_password: this.newApiObj.authPassword,
        };

        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.CreateAPI(payload);
        this.newApiObj = {};

        this.apiData = await this.GetAPI();
        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("create-new-api-modal");
        Swal.fire("API Created", "API created successfully", "success");
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", e.response.data.message, "warning");
      }
    },

    async updateApi () {
      try {

        let payload = {
          id: this.updateApiObj.id,
          api_name: this.updateApiObj.api_name,
          api_type: this.updateApiObj.api_type,
          package_id: this.updateApiObj.package_id,
          request_type: this.updateApiObj.request_type,
          request_path: this.updateApiObj.request_path,
          partial_image_url: this.updateApiObj.isPartialImageUrl,
          auth_username: this.updateApiObj.authUsername,
          auth_password: this.updateApiObj.authPassword,
        };

        this.$store.dispatch("layout/changeLoaderValue", true);
        await this.UpdateAPI(payload);

        this.updateApiObj = {};
        this.apiData = await this.GetAPI();
        this.$store.dispatch("layout/changeLoaderValue", false);
        this.$bvModal.hide("edit-api-modal");
        Swal.fire("API Updated", "API updated successfully", "success");
      } catch (e) {
        this.$store.dispatch("layout/changeLoaderValue", false);
        Swal.fire("Failed!", "Failed to update API", "error");
      }
    },

    async deleteApi (vsds) {
      try {
        const id = {
          id: vsds.id,
        };

        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
        }).then(async (result) => {
          if (result.isConfirmed) {
            Swal.fire("API Deleted!", "API has been deleted.", "success");
            await this.DeleteAPI(id);
            this.apiData = await this.GetAPI();
          }
        });
      } catch (e) {
        Swal.fire("Failed!", "Failed to delete API", "error");
      }
    },

    getPackageName (id) {
      const pkg = this.packageMaster.find(p => p.id == id)
      return (pkg) ? pkg.package_name : ''
    },

    getApiTypeClass (type) {
      switch (type) {
        case 'VIDS':
          return "bg-info";

        case 'VSDS':
          return "bg-primary";

        case 'ATCC':
          return "bg-warning";

        default:
          return "bg-danger";
      }
    },
  },
  components: {
    Layout,
    PageHeader,
  },

  async mounted () {
    this.apiData = await this.GetAPI();
    this.packageMaster = await this.GetAllPackages();
  },
};
</script>